import { Navigate, Outlet, useParams } from 'react-router-dom';

import type { NavItemConfig, RouteNavConfig } from '@revelio/auth';
import { PagePaths, PageTitles } from '@revelio/core';
import { Tab } from '@revelio/data-access';

import AdminRewrite from '../adminRewrite/admin';
import AdminClientEdit from '../adminRewrite/admin-client-edit/admin-client-edit';
import AdminUserAdd from '../adminRewrite/admin-user-add/admin-user-add';
import AdminRewriteUserEdit from '../adminRewrite/admin-user-edit/admin-user-edit';
import ClientAdmin from '../adminRewrite/client-admin';
import UserEdit from '../adminRewrite/user-edit/user-edit';
import { CustomDashRoute } from '../CustomDash/CustomDashRoute';
import { TextChartHelpPage } from '../CustomDash/components/Chart/TextChart/TextChartHelpPage';
import { companyOverview } from '../CustomDash/templates/company-overview-v2';
import { companyScreenerV2 } from '../CustomDash/templates/company-screener-v2';
import { companySentiment } from '../CustomDash/templates/company-sentiment';
import { companyComposition } from '../CustomDash/templates/companyComposition';
import { companyPay } from '../CustomDash/templates/companyPay';
import { companyPostings } from '../CustomDash/templates/companyPostings';
import { companyTransitions } from '../CustomDash/templates/companyTransitions';
import { geographyScreenerV2 } from '../CustomDash/templates/geography-screener-v2';
import { geographySentiment } from '../CustomDash/templates/geography-sentiment';
import { geographyComposition } from '../CustomDash/templates/geographyComposition';
import { geographyOverview } from '../CustomDash/templates/geographyOverview';
import { geographyPay } from '../CustomDash/templates/geographyPay';
import { geographyPostings } from '../CustomDash/templates/geographyPostings';
import { geographyTransitions } from '../CustomDash/templates/geographyTransitions';
import { roleScreenerV2 } from '../CustomDash/templates/role-screener-v2';
import { roleSentiment } from '../CustomDash/templates/role-sentiment';
import { roleComposition } from '../CustomDash/templates/roleComposition';
import { roleOverview } from '../CustomDash/templates/roleOverview';
import { rolePay } from '../CustomDash/templates/rolePay';
import { rolePostings } from '../CustomDash/templates/rolePostings';
import { roleTransitions } from '../CustomDash/templates/roleTransitions';
import { talentDiscovery } from '../CustomDash/templates/talent-discovery';
import { ANALYTICS_SERVER_TABS } from '../CustomDash/utils/revelio-primary-filter-entity-switch';
import { supportedEntityTypes } from '../CustomDash/utils/routing-helpers';
import { DataBuilder, DataBuilderPaths } from '../DataBuilder/DataBuilder';
import { DataBuilderRouteGuard } from '../DataBuilder/DataBuilderRouteGuard';
import { DataBuilderStatus } from '../DataBuilder/DataBuilderStatus';
import { DataBuilderChooseStep } from '../DataBuilder/steps/Step0Choose';
import { DataBuilderDatasetStep } from '../DataBuilder/steps/Step1Dataset';
import { DataBuilderSavedDatasetsStep } from '../DataBuilder/steps/Step1SavedDatasets';
import { DataBuilderColumnsStep } from '../DataBuilder/steps/Step2Columns';
import { DataBuilderDeliveryStep } from '../DataBuilder/steps/Step3Delivery';
import { DataBuilderConfirmationStep } from '../DataBuilder/steps/Step4Confirmation';
import { DeliverablesDataEnrichment } from '../deliverables/data-enrichment/DeliverablesDataEnrichment';
import { EntitySearch } from '../entity-search/entity-search';
import { EntitySearchResults } from '../entity-search/entity-search-results';
import { HealthCheck } from '../health-check/healthCheck';
import DataDictionary from '../iframed-pages/data-dictionary';
import TaxonomyCatalog from '../iframed-pages/taxonomy-catalog';
import { ReportType } from '../reports/report-builder/01-report-type/report-type';
import { EntityConfiguration } from '../reports/report-builder/02-entity-configuration/entity-configuration';
import { ReportConfirmation } from '../reports/report-builder/03-report-confirmation/report-confirmation';
import {
  ReportBuilderDeliverablePaths,
  ReportBuilderPage,
} from '../reports/report-builder/report-builder-page';
import ReportPage from '../reports/report-page';
import ReportsPage from '../reports/reports-page';
import SampleReportPage from '../reports/sample-report.page';
import EnrichedResumePage from '../resume-enrichment/EnrichedResumePage';
import ResumeEnrichment from '../resume-enrichment/ResumeEnrichment';
import LzStringDecoderPage from '../tools/LzStringDecoderPage';
import SnowflakeQueryPlaygroundPage from '../tools/SnowflakeQueryPlaygroundPage';
import ToolsLandingPage from '../tools/ToolsLandingPage';
import { RequireAuth } from './ProtectedRoute';
import {
  IconAnalyticsBars,
  IconApplicationsGrid,
  IconDiscoveryCompass,
  IconReportsStack,
} from './side-nav/custom-icons';
import { iconUrls } from './side-nav/icons';

const FindEntityRedirect = () => {
  const { entityType, id } = useParams();
  return <Navigate to={`/tools/find/${entityType}/${id}`} replace />;
};

const sharedAppRoutesForNav: (RouteNavConfig | NavItemConfig)[] = [
  {
    name: PageTitles.DOCUMENTATION,
    icon: iconUrls.documentation,
    divider: true,
    isSourcesTab: false,
    children: [],
  },
  {
    name: 'Admin',
    path: 'admin/:clientId?',
    admin: true,
    element: <AdminRewrite />,
  },
  {
    name: 'Admin User Edit',
    path: 'admin/user/:userId/edit',
    admin: true,
    clientAdmin: true,
    element: <AdminRewriteUserEdit />,
  },
  {
    name: 'Admin Client Edit',
    path: 'admin/client/:clientId/edit',
    admin: true,
    clientAdmin: true,
    element: <AdminClientEdit />,
  },
  {
    name: 'Admin User Add',
    path: 'admin/user/add/:clientId?',
    admin: true,
    clientAdmin: true,
    element: <AdminUserAdd />,
  },
  {
    name: 'Manage',
    path: 'manage',
    clientAdmin: true,
    element: <ClientAdmin />,
  },
  {
    name: 'Manage User Edit',
    path: 'manage/user/:userId/edit',
    element: <UserEdit />,
  },
  {
    name: 'Data Dictionary',
    path: `${PagePaths.DATA_DICTIONARY}/*`,
    serverTab: Tab.DataDictionary,
    excludeFromSideNavButtons: true,
    element: <DataDictionary />,
  },
  {
    name: 'Taxonomy Catalog',
    path: `${PagePaths.TAXONOMY_CATALOG}/*`,
    excludeFromSideNavButtons: true,
    element: <TaxonomyCatalog />,
  },
  {
    name: 'Health Check',
    path: 'health-check',
    element: <HealthCheck />,
    admin: true,
  },
  {
    name: 'Tools',
    path: 'tools',
    element: <Outlet />,
    excludeFromSideNavButtons: true,
    nestedRoutes: [
      {
        path: '',
        element: <ToolsLandingPage />,
      },
      {
        path: 'lzstring-decoder',
        element: <LzStringDecoderPage />,
      },
      {
        path: 'snowflake-query-playground',
        element: (
          <RequireAuth adminRequired>
            <SnowflakeQueryPlaygroundPage />
          </RequireAuth>
        ),
      },
      {
        path: 'find',
        element: <EntitySearch />,
      },
      {
        path: 'find/:entityType/:id',
        element: <EntitySearchResults />,
      },
    ],
  },
  {
    name: 'Text Chart Help',
    path: 'help/text-chart',
    element: <TextChartHelpPage />,
    excludeFromSideNavButtons: true,
  },
  {
    name: 'Entity Search',
    path: '/find',
    element: <Navigate to="/tools/find" replace />,
    excludeFromSideNavButtons: true,
  },
  {
    name: 'Entity Search Page',
    path: '/find/:entityType/:id',
    element: <FindEntityRedirect />,
    excludeFromSideNavButtons: true,
  },
  // Legacy path redirects
  {
    name: 'Company Composition',
    path: PagePaths.COMPANY_COMPOSITIONS_LEGACY,
    element: <CustomDashRoute idOverride={companyComposition.id} />,
    excludeFromSideNavButtons: true,
  },
  {
    name: 'Company Sentiment',
    path: PagePaths.COMPANY_SENTIMENT_RATINGS_LEGACY,
    element: <CustomDashRoute idOverride={companySentiment.id} />,
    excludeFromSideNavButtons: true,
  },
  {
    name: 'Company Sentiment',
    path: PagePaths.COMPANY_SENTIMENT_REVIEWS_LEGACY,
    element: <CustomDashRoute idOverride={companySentiment.id} />,
    excludeFromSideNavButtons: true,
  },
  {
    name: 'Geography Composition',
    path: PagePaths.GEOGRAPHY_COMPOSITIONS_LEGACY,
    element: <CustomDashRoute idOverride={geographyComposition.id} />,
    excludeFromSideNavButtons: true,
  },
  {
    name: 'Geography Sentiment',
    path: PagePaths.GEOGRAPHY_SENTIMENT_RATINGS_LEGACY,
    element: <CustomDashRoute idOverride={geographySentiment.id} />,
    excludeFromSideNavButtons: true,
  },
  {
    name: 'Geography Sentiment',
    path: PagePaths.GEOGRAPHY_SENTIMENT_REVIEWS_LEGACY,
    element: <CustomDashRoute idOverride={geographySentiment.id} />,
    excludeFromSideNavButtons: true,
  },
  {
    name: 'Role Composition',
    path: PagePaths.ROLE_COMPOSITIONS_LEGACY,
    element: <CustomDashRoute idOverride={roleComposition.id} />,
    excludeFromSideNavButtons: true,
  },
  {
    name: 'Role Sentiment',
    path: PagePaths.ROLE_SENTIMENT_RATINGS_LEGACY,
    element: <CustomDashRoute idOverride={roleSentiment.id} />,
    excludeFromSideNavButtons: true,
  },
  {
    name: 'Role Sentiment',
    path: PagePaths.ROLE_SENTIMENT_REVIEWS_LEGACY,
    element: <CustomDashRoute idOverride={roleSentiment.id} />,
    excludeFromSideNavButtons: true,
  },
  // Generic custom dash routes
  ...supportedEntityTypes.map((entityType) => ({
    name: 'Custom Dashboard',
    path: `${entityType}/:templateId?/:action?`,
    element: <CustomDashRoute />,
  })),
];

const navItemsLegacy: (RouteNavConfig | NavItemConfig)[] = [
  {
    name: PageTitles.COMPANY,
    icon: iconUrls.company,
    isSourcesTab: true,
    children: [
      {
        name: PageTitles.SUMMARY,
        serverTab: Tab.EntitySummary,
        path: PagePaths.COMPANY_SUMMARY,
        element: <CustomDashRoute idOverride={companyOverview.id} />,
        parent: PageTitles.COMPANY,
      },
      {
        name: PageTitles.COMPOSITIONS,
        serverTab: Tab.Overview,
        path: PagePaths.COMPANY_COMPOSITIONS,
        element: <CustomDashRoute idOverride={companyComposition.id} />,
        parent: PageTitles.COMPANY,
      },
      {
        name: PageTitles.TRANSITIONS,
        serverTab: Tab.Transition,
        path: PagePaths.COMPANY_TRANSITIONS,
        element: <CustomDashRoute idOverride={companyTransitions.id} />,
        parent: PageTitles.COMPANY,
      },
      {
        name: PageTitles.JOB_POSTINGS,
        serverTab: Tab.Posting,
        path: PagePaths.COMPANY_POSTINGS,
        element: <CustomDashRoute idOverride={companyPostings.id} />,
        parent: PageTitles.COMPANY,
      },
      {
        name: PageTitles.PAY,
        serverTab: Tab.Compensation,
        path: PagePaths.COMPANY_PAY,
        element: <CustomDashRoute idOverride={companyPay.id} />,
        parent: PageTitles.COMPANY,
      },
      {
        name: PageTitles.SENTIMENT,
        serverTab: Tab.Sentiment,
        path: PagePaths.COMPANY_SENTIMENT,
        element: <CustomDashRoute idOverride={companySentiment.id} />,
        parent: PageTitles.COMPANY,
      },
      {
        name: PageTitles.SCREENER,
        serverTab: Tab.Screener,
        path: PagePaths.COMPANY_SCREENER,
        element: <CustomDashRoute idOverride={companyScreenerV2.id} />,
        parent: PageTitles.COMPANY,
        divider: true,
      },
    ],
  },

  {
    name: PageTitles.GEOGRAPHY,
    icon: iconUrls.geography,
    isSourcesTab: true,
    children: [
      {
        name: PageTitles.SUMMARY,
        serverTab: Tab.EntitySummary,
        path: PagePaths.GEOGRAPHY_SUMMARY,
        element: <CustomDashRoute idOverride={geographyOverview.id} />,
        parent: PageTitles.GEOGRAPHY,
      },
      {
        name: PageTitles.COMPOSITIONS,
        serverTab: Tab.Overview,
        path: PagePaths.GEOGRAPHY_COMPOSITIONS,
        element: <CustomDashRoute idOverride={geographyComposition.id} />,
        parent: PageTitles.GEOGRAPHY,
      },
      {
        name: PageTitles.TRANSITIONS,
        serverTab: Tab.Transition,
        path: PagePaths.GEOGRAPHY_TRANSITIONS,
        element: <CustomDashRoute idOverride={geographyTransitions.id} />,
        parent: PageTitles.GEOGRAPHY,
      },
      {
        name: PageTitles.JOB_POSTINGS,
        serverTab: Tab.Posting,
        path: PagePaths.GEOGRAPHY_POSTINGS,
        element: <CustomDashRoute idOverride={geographyPostings.id} />,
        parent: PageTitles.GEOGRAPHY,
      },
      {
        name: PageTitles.PAY,
        serverTab: Tab.Compensation,
        path: PagePaths.GEOGRAPHY_PAY,
        element: <CustomDashRoute idOverride={geographyPay.id} />,
        parent: PageTitles.GEOGRAPHY,
      },
      {
        name: PageTitles.SENTIMENT,
        serverTab: Tab.Sentiment,
        path: PagePaths.GEOGRAPHY_SENTIMENT,
        element: <CustomDashRoute idOverride={geographySentiment.id} />,
        parent: PageTitles.GEOGRAPHY,
      },
      {
        name: PageTitles.SCREENER,
        serverTab: Tab.Screener,
        path: PagePaths.GEOGRAPHY_SCREENER,
        element: <CustomDashRoute idOverride={geographyScreenerV2.id} />,
        parent: PageTitles.GEOGRAPHY,
        divider: true,
      },
    ],
  },

  {
    name: PageTitles.ROLE,
    icon: iconUrls.role,
    isSourcesTab: true,
    children: [
      {
        name: PageTitles.SUMMARY,
        serverTab: Tab.EntitySummary,
        path: PagePaths.ROLE_SUMMARY,
        element: <CustomDashRoute idOverride={roleOverview.id} />,
        parent: PageTitles.ROLE,
      },
      {
        name: PageTitles.COMPOSITIONS,
        serverTab: Tab.Overview,
        path: PagePaths.ROLE_COMPOSITIONS,
        element: <CustomDashRoute idOverride={roleComposition.id} />,
        parent: PageTitles.ROLE,
      },
      {
        name: PageTitles.TRANSITIONS,
        serverTab: Tab.Transition,
        path: PagePaths.ROLE_TRANSITIONS,
        element: <CustomDashRoute idOverride={roleTransitions.id} />,
        parent: PageTitles.ROLE,
      },
      {
        name: PageTitles.JOB_POSTINGS,
        serverTab: Tab.Posting,
        path: PagePaths.ROLE_POSTINGS,
        element: <CustomDashRoute idOverride={rolePostings.id} />,
        parent: PageTitles.ROLE,
      },
      {
        name: PageTitles.PAY,
        serverTab: Tab.Compensation,
        path: PagePaths.ROLE_PAY,
        element: <CustomDashRoute idOverride={rolePay.id} />,
        parent: PageTitles.ROLE,
      },
      {
        name: PageTitles.SENTIMENT,
        serverTab: Tab.Sentiment,
        path: PagePaths.ROLE_SENTIMENT,
        element: <CustomDashRoute idOverride={roleSentiment.id} />,
        parent: PageTitles.ROLE,
      },
      {
        name: PageTitles.SCREENER,
        serverTab: Tab.Screener,
        path: PagePaths.ROLE_SCREENER,
        element: <CustomDashRoute idOverride={roleScreenerV2.id} />,
        parent: PageTitles.ROLE,
        divider: true,
      },
    ],
  },

  {
    name: PageTitles.INDIVIDUAL,
    icon: iconUrls.individuals,
    isSourcesTab: true,
    children: [
      {
        name: PageTitles.TALENT_DISCOVERY,
        serverTab: Tab.TalentDiscovery,
        path: 'talent-discovery',
        element: <CustomDashRoute idOverride={talentDiscovery.id} />,
      },
      {
        name: PageTitles.RESUME_ENRICHMENT,
        serverTab: Tab.ResumeParsing,
        path: PagePaths.RESUME_ENRICHMENT,
        element: <Outlet />,
        nestedRoutes: [
          {
            path: ':id',
            element: <EnrichedResumePage />,
            serverTab: Tab.ResumeParsing,
            parent: PageTitles.INDIVIDUAL,
          },
          {
            path: '',
            element: <ResumeEnrichment />,
            serverTab: Tab.ResumeParsing,
            parent: PageTitles.INDIVIDUAL,
          },
        ],
      },
    ],
  },
  {
    name: PageTitles.DELIVERABLES,
    icon: iconUrls.deliverables,
    isSourcesTab: true,
    divider: true,
    children: [
      {
        name: PageTitles.DATA_BUILDER,
        path: PagePaths.DATA_BUILDER,
        serverTab: Tab.Deliverables,
        element: (
          <DataBuilderRouteGuard>
            <DataBuilder />
          </DataBuilderRouteGuard>
        ),
        nestedRoutes: [
          {
            path: DataBuilderPaths.CHOOSE,
            element: <DataBuilderChooseStep />,
          },
          {
            path: DataBuilderPaths.SAVED_DATASETS,
            element: <DataBuilderSavedDatasetsStep />,
          },
          {
            path: DataBuilderPaths.DATASET,
            element: <DataBuilderDatasetStep />,
          },
          {
            path: DataBuilderPaths.COLUMNS,
            element: <DataBuilderColumnsStep />,
          },
          {
            path: DataBuilderPaths.DELIVERY,
            element: <DataBuilderDeliveryStep />,
          },
          {
            path: DataBuilderPaths.CONFIRMATION,
            element: <DataBuilderConfirmationStep />,
          },
        ],
      },
      {
        name: PageTitles.DELIVERABLES_STATUS,
        serverTab: Tab.Deliverables,
        path: PagePaths.DATA_BUILDER_STATUS,
        element: (
          <DataBuilderRouteGuard>
            <DataBuilderStatus />
          </DataBuilderRouteGuard>
        ),
      },
      {
        name: PageTitles.DATA_ENRICHMENT,
        serverTab: Tab.DataEnrichment,
        path: `${PagePaths.DELIVERABLES}/${PagePaths.DATA_ENRICHMENT}`,
        element: <DeliverablesDataEnrichment />,
      },
      {
        name: PageTitles.REPORT_BUILDER,
        path: PagePaths.REPORT_BUILDER,
        serverTab: Tab.Reports,
        subHeading: 'Reports',
        element: <ReportBuilderPage />,
        nestedRoutes: [
          {
            path: ReportBuilderDeliverablePaths.REPORT_TYPE,
            element: <ReportType />,
          },
          {
            path: ReportBuilderDeliverablePaths.ENTITY,
            element: <EntityConfiguration />,
          },
          {
            path: ReportBuilderDeliverablePaths.CONFIRMATION,
            element: <ReportConfirmation />,
          },
        ],
      },
      {
        name: PageTitles.REPORTS,
        path: PagePaths.REPORTS,
        serverTab: Tab.Reports,
        element: <Outlet />,
        nestedRoutes: [
          {
            path: '',
            element: <ReportsPage />,
            serverTab: [Tab.Deliverables, Tab.Reports],
            parent: PageTitles.REPORTS,
          },
          {
            path: ':id',
            element: <ReportPage />,
            serverTab: [Tab.Deliverables, Tab.Reports],
            parent: PageTitles.REPORTS,
          },
        ],
      },
      {
        name: PageTitles.REPORTS_SAMPLE,
        path: PagePaths.REPORTS_SAMPLE,
        element: <SampleReportPage />,
      },
    ],
  },
  ...sharedAppRoutesForNav,
];

const navItemsNewLayout: (RouteNavConfig | NavItemConfig)[] = [
  {
    name: PageTitles.DASHBOARD_NAV,
    icon: <IconAnalyticsBars />,
    isSourcesTab: true,
    // Visible rail items (Company / Geography / Role) come first; the
    // remaining per-view routes are registered with
    // `excludeFromSideNavButtons` so they're reachable by URL and still
    // count as Analytics for active-state matching, but they're surfaced
    // to the user via the page-level tab strip in the Header (see
    // AnalyticsViewTabs) instead of the rail.
    children: [
      {
        name: PageTitles.COMPANY,
        serverTab: ANALYTICS_SERVER_TABS,
        path: PagePaths.COMPANY_SUMMARY,
        element: <CustomDashRoute idOverride={companyOverview.id} />,
        parent: PageTitles.DASHBOARD_NAV,
      },
      {
        name: PageTitles.GEOGRAPHY,
        serverTab: ANALYTICS_SERVER_TABS,
        path: PagePaths.GEOGRAPHY_SUMMARY,
        element: <CustomDashRoute idOverride={geographyOverview.id} />,
        parent: PageTitles.DASHBOARD_NAV,
      },
      {
        name: PageTitles.ROLE,
        serverTab: ANALYTICS_SERVER_TABS,
        path: PagePaths.ROLE_SUMMARY,
        element: <CustomDashRoute idOverride={roleOverview.id} />,
        parent: PageTitles.DASHBOARD_NAV,
      },
      {
        name: 'Company Compositions',
        serverTab: Tab.Overview,
        path: PagePaths.COMPANY_COMPOSITIONS,
        element: <CustomDashRoute idOverride={companyComposition.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Company Transitions',
        serverTab: Tab.Transition,
        path: PagePaths.COMPANY_TRANSITIONS,
        element: <CustomDashRoute idOverride={companyTransitions.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Company Job Postings',
        serverTab: Tab.Posting,
        path: PagePaths.COMPANY_POSTINGS,
        element: <CustomDashRoute idOverride={companyPostings.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Company Pay',
        serverTab: Tab.Compensation,
        path: PagePaths.COMPANY_PAY,
        element: <CustomDashRoute idOverride={companyPay.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Company Sentiment',
        serverTab: Tab.Sentiment,
        path: PagePaths.COMPANY_SENTIMENT,
        element: <CustomDashRoute idOverride={companySentiment.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Geography Compositions',
        serverTab: Tab.Overview,
        path: PagePaths.GEOGRAPHY_COMPOSITIONS,
        element: <CustomDashRoute idOverride={geographyComposition.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Geography Transitions',
        serverTab: Tab.Transition,
        path: PagePaths.GEOGRAPHY_TRANSITIONS,
        element: <CustomDashRoute idOverride={geographyTransitions.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Geography Job Postings',
        serverTab: Tab.Posting,
        path: PagePaths.GEOGRAPHY_POSTINGS,
        element: <CustomDashRoute idOverride={geographyPostings.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Geography Pay',
        serverTab: Tab.Compensation,
        path: PagePaths.GEOGRAPHY_PAY,
        element: <CustomDashRoute idOverride={geographyPay.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Geography Sentiment',
        serverTab: Tab.Sentiment,
        path: PagePaths.GEOGRAPHY_SENTIMENT,
        element: <CustomDashRoute idOverride={geographySentiment.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Role Compositions',
        serverTab: Tab.Overview,
        path: PagePaths.ROLE_COMPOSITIONS,
        element: <CustomDashRoute idOverride={roleComposition.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Role Transitions',
        serverTab: Tab.Transition,
        path: PagePaths.ROLE_TRANSITIONS,
        element: <CustomDashRoute idOverride={roleTransitions.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Role Job Postings',
        serverTab: Tab.Posting,
        path: PagePaths.ROLE_POSTINGS,
        element: <CustomDashRoute idOverride={rolePostings.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Role Pay',
        serverTab: Tab.Compensation,
        path: PagePaths.ROLE_PAY,
        element: <CustomDashRoute idOverride={rolePay.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
      {
        name: 'Role Sentiment',
        serverTab: Tab.Sentiment,
        path: PagePaths.ROLE_SENTIMENT,
        element: <CustomDashRoute idOverride={roleSentiment.id} />,
        parent: PageTitles.DASHBOARD_NAV,
        excludeFromSideNavButtons: true,
      },
    ],
  },
  {
    name: PageTitles.DISCOVERY_NAV,
    icon: <IconDiscoveryCompass />,
    isSourcesTab: true,
    children: [
      {
        name: 'Individual',
        serverTab: Tab.TalentDiscovery,
        path: 'talent-discovery',
        element: <CustomDashRoute idOverride={talentDiscovery.id} />,
        parent: PageTitles.DISCOVERY_NAV,
      },
      {
        name: 'Company',
        serverTab: Tab.Screener,
        path: PagePaths.COMPANY_SCREENER,
        element: <CustomDashRoute idOverride={companyScreenerV2.id} />,
        parent: PageTitles.DISCOVERY_NAV,
      },
      {
        name: 'Geography',
        serverTab: Tab.Screener,
        path: PagePaths.GEOGRAPHY_SCREENER,
        element: <CustomDashRoute idOverride={geographyScreenerV2.id} />,
        parent: PageTitles.DISCOVERY_NAV,
      },
      {
        name: 'Role',
        serverTab: Tab.Screener,
        path: PagePaths.ROLE_SCREENER,
        element: <CustomDashRoute idOverride={roleScreenerV2.id} />,
        parent: PageTitles.DISCOVERY_NAV,
      },
    ],
  },
  {
    name: PageTitles.APPLICATIONS_NAV,
    icon: <IconApplicationsGrid />,
    isSourcesTab: true,
    children: [
      {
        name: PageTitles.DATA_ENRICHMENT,
        serverTab: Tab.DataEnrichment,
        path: `${PagePaths.DELIVERABLES}/${PagePaths.DATA_ENRICHMENT}`,
        element: <DeliverablesDataEnrichment />,
      },
      {
        name: PageTitles.RESUME_ENRICHMENT,
        serverTab: Tab.ResumeParsing,
        path: PagePaths.RESUME_ENRICHMENT,
        element: <Outlet />,
        nestedRoutes: [
          {
            path: ':id',
            element: <EnrichedResumePage />,
            serverTab: Tab.ResumeParsing,
            parent: PageTitles.APPLICATIONS_NAV,
          },
          {
            path: '',
            element: <ResumeEnrichment />,
            serverTab: Tab.ResumeParsing,
            parent: PageTitles.APPLICATIONS_NAV,
          },
        ],
      },
    ],
  },
  {
    name: PageTitles.REPORTS_NAV,
    icon: <IconReportsStack />,
    isSourcesTab: true,
    children: [
      {
        name: PageTitles.REPORT_BUILDER,
        path: PagePaths.REPORT_BUILDER,
        serverTab: Tab.Reports,
        subHeading: 'Reports',
        element: <ReportBuilderPage />,
        nestedRoutes: [
          {
            path: ReportBuilderDeliverablePaths.REPORT_TYPE,
            element: <ReportType />,
          },
          {
            path: ReportBuilderDeliverablePaths.ENTITY,
            element: <EntityConfiguration />,
          },
          {
            path: ReportBuilderDeliverablePaths.CONFIRMATION,
            element: <ReportConfirmation />,
          },
        ],
      },
      {
        name: PageTitles.REPORTS_STATUS,
        path: PagePaths.REPORTS,
        serverTab: Tab.Reports,
        element: <Outlet />,
        nestedRoutes: [
          {
            path: '',
            element: <ReportsPage />,
            serverTab: [Tab.Deliverables, Tab.Reports],
            parent: PageTitles.REPORTS_NAV,
          },
          {
            path: ':id',
            element: <ReportPage />,
            serverTab: [Tab.Deliverables, Tab.Reports],
            parent: PageTitles.REPORTS_NAV,
          },
        ],
      },
      {
        name: PageTitles.REPORTS_SAMPLE,
        path: PagePaths.REPORTS_SAMPLE,
        element: <SampleReportPage />,
      },
    ],
  },
  {
    name: PageTitles.DELIVERABLES,
    icon: iconUrls.deliverables,
    isSourcesTab: true,
    divider: true,
    children: [
      {
        name: PageTitles.DATA_BUILDER,
        path: PagePaths.DATA_BUILDER,
        serverTab: Tab.Deliverables,
        element: (
          <DataBuilderRouteGuard>
            <DataBuilder />
          </DataBuilderRouteGuard>
        ),
        nestedRoutes: [
          {
            path: DataBuilderPaths.CHOOSE,
            element: <DataBuilderChooseStep />,
          },
          {
            path: DataBuilderPaths.SAVED_DATASETS,
            element: <DataBuilderSavedDatasetsStep />,
          },
          {
            path: DataBuilderPaths.DATASET,
            element: <DataBuilderDatasetStep />,
          },
          {
            path: DataBuilderPaths.COLUMNS,
            element: <DataBuilderColumnsStep />,
          },
          {
            path: DataBuilderPaths.DELIVERY,
            element: <DataBuilderDeliveryStep />,
          },
          {
            path: DataBuilderPaths.CONFIRMATION,
            element: <DataBuilderConfirmationStep />,
          },
        ],
      },
      {
        name: PageTitles.DELIVERABLES_STATUS,
        serverTab: Tab.Deliverables,
        path: PagePaths.DATA_BUILDER_STATUS,
        element: (
          <DataBuilderRouteGuard>
            <DataBuilderStatus />
          </DataBuilderRouteGuard>
        ),
      },
    ],
  },
  ...sharedAppRoutesForNav,
];

export function getNavItems(
  isNewDashboardLayout: boolean
): (RouteNavConfig | NavItemConfig)[] {
  return isNewDashboardLayout ? navItemsNewLayout : navItemsLegacy;
}
